import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { type SessionResponse } from '@heidi/session/contracts';

/**
 * Whether a session already has a finished (stopped) recording — i.e. it has
 * been recorded and is no longer active.
 *
 * This is the desktop equivalent of iOS's terminal `HHSessionRecordingState`
 * value `.closed`. The desktop `RecordingStatus` enum has no terminal state
 * (only `IDLE | PAUSED | RECORDING`), so a stopped session reports `IDLE` —
 * indistinguishable from a never-recorded draft. We instead detect "already
 * recorded" via `duration`, which is backend-persisted (survives app restart),
 * with `audio[]` as a secondary signal where the query selected it. There is
 * no explicit `ended_at` / `completed_at` / terminal-status field on the
 * session, so `duration` is the most authoritative finished signal available.
 *
 * Note: this checks recording completion only — it does NOT verify the session
 * was a Chronicle recording. Chronicle-remote scoping is provided by the call
 * site (the device-initiated start handler runs only when the Heidi Remote is
 * the selected input).
 *
 * An actively recording or paused session is deliberately NOT "finished": the
 * live pause↔resume cycle must keep working while a recording is in flight.
 *
 * Used by the Heidi Remote flow to refuse resuming a finished session — a
 * remote press on a finished session starts a brand-new session instead.
 */
export function isFinishedRecordingSession(
  session: Pick<SessionResponse, 'recording_status' | 'duration' | 'audio'> | undefined
): boolean {
  if (!session) return false;

  if (
    session.recording_status === RecordingStatus.RECORDING ||
    session.recording_status === RecordingStatus.PAUSED
  ) {
    return false;
  }

  return (session.duration ?? 0) > 0 || (session.audio?.length ?? 0) > 0;
}
